/**
 * Workspace Type Guards
 *
 * Restrict access to routes based on workspace type (service providers vs
 * property managers vs tenants vs syndic vs agency).
 *
 * Reliability note (2026-06-09): these guards used to gate on
 * `toObservable(currentWorkspace).pipe(filter(w => w !== null), take(1),
 * timeout(10000))`. That had two real problems felt by users:
 *   1. "I click and nothing happens, then it unblocks" — navigation hung for up
 *      to 10s whenever the workspace signal lagged hydration (or desynced from
 *      the authoritative subject), with no feedback.
 *   2. On timeout the guard fell OPEN (`return true`), letting a restricted
 *      route through.
 * They now await the service's tuned `waitForWorkspace()` (bounded ~4s, triggers
 * init, resolves the INSTANT the workspace id is available from the authoritative
 * subject). In the common case (workspace already hydrated) this resolves
 * synchronously — zero added latency. When no workspace resolves in time
 * (transient login/onboarding state) the type is `null` and the guard defers to
 * the app's default routing / onboarding flow rather than mis-routing.
 *
 * @version 2.0.0
 */

import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { TranslateService } from '@ngx-translate/core';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { UIFeedbackService } from '@shared/services/ui-feedback.service';
import { WorkspaceType } from '@shared/definitions/workspace';
// `Workspace` aligned with the WorkspaceStore's signal type (workspace.service)
// so `loadCandidates` returns exactly what `store.userWorkspaces()` yields.
import type { Workspace } from '@core/services/auth/workspace.service';

/**
 * Resolve the active workspace's type for a guard decision.
 * Returns `null` if no workspace hydrates within the bounded wait — callers
 * should treat that as "can't decide yet" and let onboarding/page guards run.
 */
async function resolveWorkspaceType(store: WorkspaceStore): Promise<WorkspaceType | null> {
  try {
    await store.waitForWorkspace();
  } catch {
    return null;
  }
  return (store.currentWorkspace()?.type || 'personal') as WorkspaceType;
}

/**
 * Deep-link auto-switch: when the active workspace is the WRONG type for the
 * route (e.g. the user follows a tenant link from an email while their active
 * workspace is their owner one), don't bounce them — if they actually OWN a
 * workspace of the required type, switch to it and let the route through.
 *
 * Without this, type guards redirect to `/`, the user lands in the wrong
 * portal, and the deep-linked resource (repair, contract, …) appears missing
 * or half-rendered — the "weird bugs" of arriving on the wrong workspace.
 *
 * `switchWorkspace` only updates the active-workspace state (no page reload),
 * so it is safe to await inside a guard.
 */
const typeOf = (w: { type?: string }): WorkspaceType => (w.type || 'personal') as WorkspaceType;

/** Load the user's workspaces, hydrating the list on a cold deep-link arrival. */
async function loadCandidates(store: WorkspaceStore, auth: Auth): Promise<readonly Workspace[]> {
  let candidates = store.userWorkspaces();
  if (candidates.length === 0 && auth.currentUser) {
    try {
      await store.loadUserWorkspaces(auth.currentUser.uid);
    } catch {
      /* ignore — fall through with whatever we have */
    }
    candidates = store.userWorkspaces();
  }
  return candidates;
}

async function doSwitch(store: WorkspaceStore, workspaceId: string): Promise<boolean> {
  if (store.currentWorkspaceId() === workspaceId) return true;
  try {
    await store.switchWorkspace(workspaceId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Honor the deep link's explicit `?ws=<id>` target. STRICT: switches only if
 * the user owns that exact workspace AND it satisfies the route's type
 * predicate — no first-match fallback, so a stale/foreign `ws` never drags the
 * user off a workspace that's already valid. Resolves the
 * multi-workspaces-of-the-same-type case (e.g. a tenant of two landlords).
 */
async function switchToExactWorkspace(
  store: WorkspaceStore,
  auth: Auth,
  workspaceId: string,
  matches: (type: WorkspaceType) => boolean,
): Promise<boolean> {
  const candidates = await loadCandidates(store, auth);
  const match = candidates.find((w) => w.workspaceId === workspaceId && matches(typeOf(w)));
  return match?.workspaceId ? doSwitch(store, match.workspaceId) : false;
}

/**
 * Deep-link auto-switch fallback: when the active workspace is the WRONG type
 * for the route, switch to the FIRST owned workspace of a valid type rather
 * than bouncing the user to `/`. Returns false when the user owns no such
 * workspace (caller then bounces).
 */
async function switchToMatchingWorkspace(
  store: WorkspaceStore,
  auth: Auth,
  matches: (type: WorkspaceType) => boolean,
): Promise<boolean> {
  const candidates = await loadCandidates(store, auth);
  const match = candidates.find((w) => matches(typeOf(w)));
  return match?.workspaceId ? doSwitch(store, match.workspaceId) : false;
}

/** Read the deep-link `?ws=<id>` workspace target from the route, if present. */
function preferredWsParam(route: ActivatedRouteSnapshot): string | null {
  return route.queryParamMap.get('ws');
}

/** Map a workspace type to the dashboard it belongs on. */
function dashboardUrlForType(router: Router, workspaceType: WorkspaceType): UrlTree {
  switch (workspaceType) {
    case 'service_provider':
      return router.createUrlTree(['/provider/dashboard']);
    case 'tenant':
      return router.createUrlTree(['/tenant/dashboard']);
    case 'syndic':
      return router.createUrlTree(['/syndic/dashboard']);
    case 'agency':
      return router.createUrlTree(['/agency/dashboard']);
    default:
      // Property managers (personal / professional) live on the main dashboard.
      return router.createUrlTree(['/']);
  }
}

@Injectable({
  providedIn: 'root'
})
export class WorkspaceTypeGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const allowedTypes: WorkspaceType[] = route.data['allowedWorkspaceTypes'] || [];
    const deniedTypes: WorkspaceType[] = route.data['deniedWorkspaceTypes'] || [];
    const isValid = (t: WorkspaceType) =>
      (deniedTypes.length === 0 || !deniedTypes.includes(t)) &&
      (allowedTypes.length === 0 || allowedTypes.includes(t));

    // Honor an explicit ?ws= deep-link target first (switches only to an owned,
    // route-valid workspace; no-op otherwise).
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, isValid)) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    // No workspace resolved yet — defer to onboarding/page guards.
    if (currentType === null) return true;

    if (!isValid(currentType)) {
      if (await switchToMatchingWorkspace(this.workspaceStore, this.auth, isValid)) {
        return true;
      }
      return dashboardUrlForType(this.router, currentType);
    }
    return true;
  }
}

/**
 * Property Manager Guard - shorthand for routes that require property manager workspace
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyManagerGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);
  private uiFeedback = inject(UIFeedbackService);
  private translate = inject(TranslateService);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    // Redirect the dedicated non-PM workspace types to their own dashboards.
    // Everything else (personal/professional/household/business) is allowed —
    // mirrors the pre-refactor behaviour exactly.
    const NON_PM_TYPES: WorkspaceType[] = ['service_provider', 'tenant', 'syndic', 'agency'];
    const isPm = (t: WorkspaceType) => !NON_PM_TYPES.includes(t);

    // ONLY honor an explicit ?ws= PM target here. We deliberately do NOT
    // first-match-switch to a PM workspace: `/` is the default post-login
    // landing and EVERY user owns an auto-created personal (PM) workspace, so a
    // blind switch would hijack a tenant/provider returning to their portal and
    // dump them on an empty PM dashboard. Without `ws`, a non-PM active
    // workspace is redirected to its own dashboard (original behaviour).
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, isPm)) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (NON_PM_TYPES.includes(currentType)) {
      // Le renvoi reste, mais il s'explique : un lien gestionnaire suivi depuis
      // un autre espace atterrissait sur le tableau de bord sans un mot (#727).
      this.uiFeedback.toast.info(this.translate.instant('workspaceGuard.wrongSpace'));
      return dashboardUrlForType(this.router, currentType);
    }
    return true;
  }
}

/**
 * Property-Manager-OR-Syndic Guard — for the shared operational workflows
 * (interventions / repairs / maintenance) that BOTH property managers and
 * syndics use. Syndics raise the same work tickets on their buildings/units, so
 * they are allowed here even though they are excluded from the property-centric
 * PM routes (properties / tenants / contracts).
 *
 * Mirrors {@link PropertyManagerGuard} exactly, only widening the allow-set to
 * include `syndic`.
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyManagerOrSyndicGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    // syndic is allowed here (unlike PropertyManagerGuard); the other dedicated
    // non-PM types still go to their own dashboards.
    const DENIED_TYPES: WorkspaceType[] = ['service_provider', 'tenant', 'agency'];
    const isAllowed = (t: WorkspaceType) => !DENIED_TYPES.includes(t);

    // Only honor an explicit ?ws= target (no blind first-match switch — same
    // rationale as PropertyManagerGuard).
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, isAllowed)) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (DENIED_TYPES.includes(currentType)) {
      return dashboardUrlForType(this.router, currentType);
    }
    return true;
  }
}

/**
 * Service Provider Guard - shorthand for routes that require service provider workspace
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceProviderGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, (t) => t === 'service_provider')) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (currentType !== 'service_provider') {
      if (await switchToMatchingWorkspace(this.workspaceStore, this.auth, (t) => t === 'service_provider')) {
        return true;
      }
      return this.router.createUrlTree(['/']);
    }
    return true;
  }
}

/**
 * Syndic Workspace Guard — restricts access to /syndic/* routes to workspaces
 * of type 'syndic'. Mirrors `ServiceProviderGuard`.
 */
@Injectable({
  providedIn: 'root'
})
export class SyndicWorkspaceGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, (t) => t === 'syndic')) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (currentType !== 'syndic') {
      if (await switchToMatchingWorkspace(this.workspaceStore, this.auth, (t) => t === 'syndic')) {
        return true;
      }
      return this.router.createUrlTree(['/']);
    }
    return true;
  }
}

/**
 * Agency Workspace Guard — restricts access to /agency/* routes to workspaces
 * of type 'agency'. Mirrors `SyndicWorkspaceGuard`.
 */
@Injectable({
  providedIn: 'root'
})
export class AgencyWorkspaceGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, (t) => t === 'agency')) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (currentType !== 'agency') {
      if (await switchToMatchingWorkspace(this.workspaceStore, this.auth, (t) => t === 'agency')) {
        return true;
      }
      return this.router.createUrlTree(['/']);
    }
    return true;
  }
}

/**
 * Tenant Workspace Guard - shorthand for routes that require tenant workspace
 */
@Injectable({
  providedIn: 'root'
})
export class TenantWorkspaceGuard implements CanActivate {
  private router = inject(Router);
  private workspaceStore = inject(WorkspaceStore);
  private auth = inject(Auth);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const ws = preferredWsParam(route);
    if (ws && await switchToExactWorkspace(this.workspaceStore, this.auth, ws, (t) => t === 'tenant')) {
      return true;
    }

    const currentType = await resolveWorkspaceType(this.workspaceStore);
    if (currentType === null) return true;

    if (currentType !== 'tenant') {
      if (await switchToMatchingWorkspace(this.workspaceStore, this.auth, (t) => t === 'tenant')) {
        return true;
      }
      return this.router.createUrlTree(['/']);
    }
    return true;
  }
}
